import {Box, Tooltip, Typography} from '@mui/material';
import {DirectionsCarOutlined} from '@mui/icons-material';
import {GridColumns} from '@mui/x-data-grid';
import {FormattedMessage} from 'react-intl';
import {Link} from 'react-router-dom';
import {MinimalUserDTO, TransactionDTO} from 'src/types';
import {formatPrice} from 'src/utils/helper';
import {getEvDriverNameFromMinimal} from 'src/utils/userNameFormatter';
import {UserTypeEnum} from 'src/enums';
import {managerTypes} from 'src/utils/consts';

// Which page a session's driver opens depends on what kind of user they are, so the rule is exported
// rather than copied: the phone card and this column have to lead to the same record.
export const getUserLink = (user: MinimalUserDTO, businessId?: number): string => {
    if (!user) return '/app/users';

    const userType = user.userType;

    // By type, not by the first of their roles: somebody can hold several now, and the legacy
    // `roles[0].role` this read is gone from the payload entirely - the branch had stopped firing.
    if (userType && managerTypes.includes(userType)) {
        return `/app/technical-team/${user.id}`;
    }

    // Employee users - use employee.id, not user.id
    if (userType === UserTypeEnum.EMPLOYEE) {
        const employeeId = user.employee?.id ?? user.id ?? 0;
        const companyId = user.employee?.company?.id || user.company?.id || 0;
        return `/app/users/employee/${employeeId}?businessId=${businessId || 0}&companyId=${companyId}`;
    }

    // Default: DRIVER, COMPANY, BUSINESS, etc.
    return `/app/users/${user.id}`;
};

export const columnConfigTransactions = (businessId?: number): GridColumns => [
    {
        field: 'user',
        renderHeader: () => <FormattedMessage id={'user'}/>,
        flex: 1,
        cellClassName: 'ampTableCell',
        renderCell: (params) => {
            const data = params?.row as TransactionDTO;
            const name = getEvDriverNameFromMinimal(data?.user);
            const link = getUserLink(data?.user, businessId);

            return (
                <Link to={link}>
                    <Typography color="primary.dark">{name}</Typography>
                </Link>
            );
        },
    },
    {
        field: 'idTag',
        renderHeader: (_params) => <FormattedMessage id={'rfid'}/>,
        flex: 1,
        cellClassName: 'ampTableCell',
        valueGetter: (params) => params.row?.idTag?.idTag,
        renderCell: (params) => {
            const idTagValue = params.row?.idTag?.idTag;
            const licensePlate = params.row?.idTag?.licensePlate;
            return (
                <Box sx={{display: 'flex', flexDirection: 'column', minWidth: 0, lineHeight: 1.2}}>
                    <Typography
                        sx={{
                            fontSize: '0.875rem',
                            color: 'text.primary',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {idTagValue || '-'}
                    </Typography>
                    {licensePlate && (
                        <Tooltip title={licensePlate}>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, mt: 0.25}}>
                                <DirectionsCarOutlined
                                    sx={{
                                        fontSize: 14,
                                        color: 'primary.main',
                                        flexShrink: 0,
                                    }}
                                />
                                <Typography
                                    sx={{
                                        fontSize: '0.72rem',
                                        fontWeight: 600,
                                        color: 'neutral.800',
                                        lineHeight: 1.3,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {licensePlate}
                                </Typography>
                            </Box>
                        </Tooltip>
                    )}
                </Box>
            );
        },
    },
    {
        field: 'startTimestamp',
        renderHeader: (_params) => <FormattedMessage id={'transactionStartColumnHeader'}/>,
        flex: 1,
        cellClassName: 'ampTableCell',
        valueGetter: (params) =>
            params.row.startTimestamp && new Date(params.row.startTimestamp).toLocaleString(),
    },
    {
        field: 'stopTimestamp',
        renderHeader: (_params) => <FormattedMessage id={'transactionEndColumnHeader'}/>,
        flex: 1,
        cellClassName: 'ampTableCell',
        valueGetter: (params) =>
            params.row.stopTimestamp && new Date(params.row.stopTimestamp).toLocaleString(),
    },
    {
        field: 'powerConsumed',
        renderHeader: (_params) => <FormattedMessage id={'powerConsumedColumnHeader'}/>,
        flex: 1,
        cellClassName: 'ampTableCell',
        valueGetter: (params) =>
            params.row.transactionSummary &&
            (params.row.transactionSummary.wattsConsumed / 1000).toFixed(3),
    },
    {
        field: 'cost',
        renderHeader: (_params) => <FormattedMessage id={'costColumnHeader'}/>,
        flex: 1,
        cellClassName: 'ampTableCell',
        valueGetter: (params) => {
            const cost = params.row?.cost;
            if (cost?.totalCost !== undefined && cost?.totalCost !== null) {
                const amount = formatPrice(cost.totalCost);
                const currency = cost.currency || 'CHF';
                return `${amount} ${currency}`;
            }
            return '-';
        },
    },
];
